
import React, { Suspense, lazy, memo, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { Box } from '@mui/material';
import { FormProvider } from './contexts/FormContext';
import { NavGuardProvider } from './contexts/NavGuardContext';
import { ThemeProvider as ColorSkinProvider } from './theme/themeConfig';
import AppThemeProvider from './theme/ThemeProvider';
import PageSpinner from './components/ui/PageSpinner';
import ConfirmDialog from './components/ui/ConfirmDialog';
import AccessibilityWidget from './components/a11y/AccessibilityWidget';
import { onSessionExpired, forceLogout } from './utils/axios';
import { looksLikeResetLink } from './utils/resetLink';



/* ── Shell (shared) ── */
const Header = lazy(() => import('./components/lander_page/Header'));
const Footer = lazy(() => import('./components/lander_page/Footer'));

/* ── Public pages ── */
const Home          = lazy(() => import('./components/lander_page/Home'));
const About         = lazy(() => import('./components/lander_page/About'));
const Faq           = lazy(() => import('./components/lander_page/Faq'));
const Login         = lazy(() => import('./components/auth/Login'));
const Register      = lazy(() => import('./components/auth/Register'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));

/* ── Applicant (protected) — one chunk ── */
const ApplicantLayout = lazy(() => import('./components/applicant/ApplicantLayout'));
const NewApplication  = lazy(() => import('./components/applicant/NewApplication'));
const Reports         = lazy(() => import('./components/applicant/Reports'));
const TrackApplication = lazy(() => import('./components/applicant/TrackApplication'));
const Payment         = lazy(() => import('./components/applicant/Payment'));
const PaymentSuccess  = lazy(() => import('./components/applicant/PaymentSuccess'));
const PaymentFailed   = lazy(() => import('./components/applicant/PaymentFailed'));


/* Suspense fallback for lazy routes. manageFocus is off here: React unmounts a
   Suspense fallback the instant the chunk resolves, and the route that mounts
   behind it owns focus placement — the spinner must not tug it back. */
const RouteLoader = <PageSpinner open message="Loading…" manageFocus={false} />;


function isTokenValid() {
  try {
    const token = sessionStorage.getItem('access-token');
    if (!token) return false;
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    // Give a 30-second grace buffer for clock skew
    return typeof exp === 'number' && exp * 1000 > Date.now() + 30_000;
  } catch {
    return false;
  }
}

function clearStaleSession() {
  try {
    sessionStorage.clear();
    localStorage.removeItem('userInfo');
  } catch { /* ignore */ }
}

const ProtectedRoute = memo(({ children }) => {
  const authenticated =
    isTokenValid() && Boolean(sessionStorage.getItem('userInfo'));

  if (!authenticated) {
    clearStaleSession();
    return <Navigate to="/login" replace />;
  }

  return children;
});
ProtectedRoute.displayName = 'ProtectedRoute';


/**
 * Rescues a password-reset link that arrived at the wrong route.
 *
 * The backend builds the reset email against the site root rather than this
 * app's base path, and mail gateways rewrite links besides — so the token
 * routinely turns up on a URL that has no reset route behind it. Previously
 * those fell through to the catch-all and were bounced to Home, silently
 * discarding the token and leaving the user staring at the landing page with
 * no idea why.
 *
 * If a URL carries something that unambiguously looks like a reset token, send
 * it to the reset page with the query and fragment intact; otherwise render
 * whatever that route was going to render anyway.
 */
const ResetLinkRescue = memo(({ children }) => {
  const { search, hash } = useLocation();
  if (looksLikeResetLink(search, hash)) {
    return <Navigate to={`/reset-password${search || ''}${hash || ''}`} replace />;
  }
  return children;
});
ResetLinkRescue.displayName = 'ResetLinkRescue';

const PublicLayout = memo(() => (
  <>
    {/* Header gets its own boundary — shell loads fast */}
    <Suspense fallback={null}>
      <Header />
    </Suspense>

    <Box
      component="main"
      id="main-content"
      tabIndex={-1}
      sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
    >
      {/* Each public page has an individual boundary */}
      <Suspense fallback={RouteLoader}>
        <Outlet />
      </Suspense>
    </Box>

    <Suspense fallback={null}>
      <Footer />
    </Suspense>
  </>
));
PublicLayout.displayName = 'PublicLayout';

/* ── Session Expired Modal ── */
function SessionExpiredModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    return onSessionExpired(() => setOpen(true));
  }, []);

  const handleLogin = () => {
    setOpen(false);
    forceLogout();
  };

  return (
    <ConfirmDialog
      open={open}
      variant="info"
      icon="🔒"
      title="Session Expired"
      message={
        <>
          For your security, you have been signed out due to inactivity or an expired session.
          <br />
          Please sign in again to continue where you left off.
        </>
      }
      confirmLabel="Sign In Again"
      hideCancel
      dismissable={false}
      onConfirm={handleLogin}
    />
  );
}

export default function App() {
  return (
    <AppThemeProvider>
      {/* First tab stop on every page — lets keyboard / screen-reader users
          jump straight past the header & sidebar (targets #main-content,
          present in both PublicLayout and ApplicantLayout). */}
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <SessionExpiredModal />
      {/* Accessibility centre — global, outside the filterable wrapper so its
          own controls are never affected by contrast/invert/grayscale filters */}
      <AccessibilityWidget />
      <div id="app-a11y-root">
      <FormProvider>
       <NavGuardProvider>
        <BrowserRouter basename="" future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            {/* ── Public routes ── */}
            <Route element={<PublicLayout />}>
              {/* A link built against the site root lands here, token in tow. */}
              <Route index element={
                <ResetLinkRescue>
                  <Suspense fallback={RouteLoader}><Home /></Suspense>
                </ResetLinkRescue>
              } />
              <Route path="about" element={
                <Suspense fallback={RouteLoader}><About /></Suspense>
              } />
              <Route path="faq" element={
                <Suspense fallback={RouteLoader}><Faq /></Suspense>
              } />

              {/*
                Login & Register skip the PublicLayout header so they render
                full-screen. Keep them INSIDE the public Route group for URL
                matching, but render without shell by nesting Suspense directly.
              */}
              <Route path="login" element={
                <Suspense fallback={RouteLoader}><Login /></Suspense>
              } />
              <Route path="register"            element={
                <Suspense fallback={RouteLoader}><Register /></Suspense>
              } />
              <Route path="register/applicant"  element={
                <Suspense fallback={RouteLoader}><Register /></Suspense>
              } />

              {/* Reset-password — all variants collapse to one component */}
              <Route path="reset-password"                  element={<Suspense fallback={RouteLoader}><ResetPassword /></Suspense>} />
              <Route path="reset-password/*"                element={<Suspense fallback={RouteLoader}><ResetPassword /></Suspense>} />
              <Route path="reset-password/:token"           element={<Suspense fallback={RouteLoader}><ResetPassword /></Suspense>} />
              <Route path="reset-password/:token/:email"    element={<Suspense fallback={RouteLoader}><ResetPassword /></Suspense>} />

              {/* Spellings the backend / mail templates have been seen to use.
                  Cheap to accept, and each one is a link that would otherwise
                  dead-end on the landing page. */}
              {['resetPassword', 'reset_password', 'password-reset', 'forgot-password', 'new-password', 'set-password'].map(alias => (
                <Route key={alias} path={`${alias}/*`} element={<Suspense fallback={RouteLoader}><ResetPassword /></Suspense>} />
              ))}
            </Route>

            <Route
              path="/applicant"
              element={
                <ProtectedRoute>
                  <ColorSkinProvider>
                  <Suspense fallback={RouteLoader}>
                    <ApplicantLayout />
                  </Suspense>
                  </ColorSkinProvider>
                </ProtectedRoute>
              }
            >
              <Route path="new"       element={<Suspense fallback={RouteLoader}><NewApplication /></Suspense>} />
              <Route path="track"     element={<Suspense fallback={RouteLoader}><TrackApplication /></Suspense>} />
              <Route path="reports"   element={<Suspense fallback={RouteLoader}><Reports /></Suspense>} />
              {/* Analytics was retired from the applicant area — the sidebar no longer
                  offers it. The path stays only to send old bookmarks to the dashboard
                  instead of dropping them on the public landing page via the catch-all. */}
              <Route path="analytics" element={<Navigate to="/applicant" replace />} />
              <Route path="payment"   element={<Suspense fallback={RouteLoader}><Payment /></Suspense>} />
              <Route path="profile" element={<Navigate to="/applicant" replace />} />
              
              {/* Payment result pages */}
              <Route path="payment/success" element={<Suspense fallback={RouteLoader}><PaymentSuccess /></Suspense>} />
              <Route path="payment/failed"  element={<Suspense fallback={RouteLoader}><PaymentFailed /></Suspense>} />
            </Route>

            {/* ── Catch-all ── */}
            <Route path="*" element={
              <ResetLinkRescue><Navigate to="/" replace /></ResetLinkRescue>
            } />
          </Routes>
        </BrowserRouter>
       </NavGuardProvider>
      </FormProvider>
      </div>
    </AppThemeProvider>
  );
}